import { Matrix4, Quaternion, Vector3 } from 'three';
import {
  FOREARM,
  UPPER_ARM,
  contactPointWorld,
  fingertipWorld,
  type ArmRig,
  type Rig,
} from './rig';
import {
  HAND_SHAPES,
  LOCATIONS,
  PALM_NORMALS,
  blendNonManual,
  blendShapes,
  easeInOut,
  lerp,
  withDefaults,
  type HandShape,
} from './pose';
import type { ContactPoint, FingerName, HandKey, Keyframe, NonManual, PalmDirection } from './types';

/**
 * One end of a hand's motion. Contact targets can only be resolved against the
 * weak hand after it is solved, so a blended pose keeps both ends and the blend
 * amount — that is what lets a movement letter glide from one contact point to
 * the next instead of snapping between them.
 */
interface HandTarget {
  /** Named location plus authored offset — used when there is no contact. */
  base: Vector3;
  /** The authored offset alone, re-applied after contact resolution. */
  offset: Vector3;
  contact: ContactPoint | null;
  /** Which finger of this hand does the touching. */
  touchWith: FingerName;
  weakHandRelative: boolean;
}

/** A keyframe resolved to concrete values, ready to blend. */
interface ResolvedHand {
  shape: HandShape;
  palm: Vector3;
  aim: Vector3 | null;
  from: HandTarget;
  to: HandTarget;
  blend: number;
}

export interface ResolvedPose {
  left: ResolvedHand;
  right: ResolvedHand;
  nmf: Required<NonManual>;
}

const MAX_CURL: Record<'proximal' | 'middle' | 'distal', number> = {
  proximal: 1.5708,
  middle: 1.7453,
  distal: 1.2217,
};
const THUMB_CURL = { proximal: 0.8727, middle: 1.0472, distal: 0.9599 };
const SPREAD_STEP = 0.2443;
const FINGER_ORDER: FingerName[] = ['index', 'middle', 'ring', 'little'];

const REST_KEY: HandKey = { shape: 'relaxed', location: 'rest', palm: 'in' };

/** Reused across frames to keep the render loop allocation-free. */
const tmp = {
  shoulder: new Vector3(),
  elbow: new Vector3(),
  reach: new Vector3(),
  direction: new Vector3(),
  upper: new Vector3(),
  axis: new Vector3(),
  localA: new Vector3(),
  localB: new Vector3(),
  wrist: new Vector3(),
  aim: new Vector3(),
  contactAim: new Vector3(),
  touch: new Vector3(),
  target: new Vector3(),
  targetB: new Vector3(),
  basisX: new Vector3(),
  basisY: new Vector3(),
  basisZ: new Vector3(),
  matrix: new Matrix4(),
  quat: new Quaternion(),
  parent: new Quaternion(),
};

/** Elbows fall down and back, away from the body — the human resting bias. */
const POLE = {
  left: new Vector3(-1, -0.55, -0.35).normalize(),
  right: new Vector3(1, -0.55, -0.35).normalize(),
};

function locationFor(key: HandKey, side: 'left' | 'right'): Vector3 {
  const [x, y, z] = LOCATIONS[key.location];
  const mirror = side === 'right' ? 1 : -1;
  const point = new Vector3(x * mirror, y, z);
  if (key.offset) {
    point.x += key.offset[0];
    point.y += key.offset[1];
    point.z += key.offset[2];
  }
  return point;
}

function directionFor(name: PalmDirection): Vector3 {
  const [x, y, z] = PALM_NORMALS[name];
  return new Vector3(x, y, z);
}

function resolveHand(key: HandKey | undefined, side: 'left' | 'right'): ResolvedHand {
  const resolved = key ?? REST_KEY;
  const shape = HAND_SHAPES[resolved.shape];
  const target: HandTarget = {
    base: locationFor(resolved, side),
    offset: resolved.offset
      ? new Vector3(resolved.offset[0], resolved.offset[1], resolved.offset[2])
      : new Vector3(),
    contact: resolved.contact ?? null,
    touchWith: resolved.touchWith ?? 'index',
    weakHandRelative: resolved.location === 'weakHand',
  };
  return {
    shape,
    palm: directionFor(resolved.palm),
    aim: resolved.aim ? directionFor(resolved.aim) : null,
    from: target,
    to: target,
    blend: 1,
  };
}

export function resolveKeyframe(frame: Keyframe): ResolvedPose {
  return {
    left: resolveHand(frame.left, 'left'),
    right: resolveHand(frame.right, 'right'),
    nmf: withDefaults(frame.nmf),
  };
}

function blendHand(from: ResolvedHand, to: ResolvedHand, amount: number): ResolvedHand {
  return {
    shape: blendShapes(from.shape, to.shape, amount),
    palm: from.palm.clone().lerp(to.palm, amount).normalize(),
    aim: to.aim && from.aim ? from.aim.clone().lerp(to.aim, amount).normalize() : (to.aim ?? from.aim),
    // Both ends are kept; the world-space targets are resolved and lerped at
    // apply time, once the weak hand is solved and contacts have positions.
    from: from.to,
    to: to.to,
    blend: amount,
  };
}

export function blendPoses(from: ResolvedPose, to: ResolvedPose, amount: number): ResolvedPose {
  return {
    left: blendHand(from.left, to.left, amount),
    right: blendHand(from.right, to.right, amount),
    nmf: blendNonManual(from.nmf, to.nmf, amount),
  };
}

/**
 * Sample a sign's keyframes at normalised time `t`, easing between holds.
 */
export function sampleKeyframes(frames: Keyframe[], t: number): ResolvedPose {
  if (frames.length === 0) return resolveKeyframe({ t: 0 });
  if (frames.length === 1) return resolveKeyframe(frames[0]!);

  const clamped = Math.min(1, Math.max(0, t));
  let index = 0;
  while (index < frames.length - 2 && frames[index + 1]!.t <= clamped) index += 1;

  const start = frames[index]!;
  const end = frames[index + 1]!;
  const span = Math.max(1e-4, end.t - start.t);
  const amount = easeInOut((clamped - start.t) / span);

  return blendPoses(resolveKeyframe(start), resolveKeyframe(end), amount);
}

/* ── applying a pose to the rig ────────────────────────────────────────── */

function applyHandShape(arm: ArmRig, shape: HandShape): void {
  const mirror = arm.side === 'right' ? 1 : -1;

  FINGER_ORDER.forEach((name, position) => {
    const finger = arm.fingers[name];
    const curl = shape.curl[name];
    // Fingers curl toward the palm, which faces the wrist's local +Z.
    finger.proximal.rotation.x = -curl * MAX_CURL.proximal;
    finger.middle.rotation.x = -curl * MAX_CURL.middle;
    finger.distal.rotation.x = -curl * MAX_CURL.distal;

    // Splay outward from the middle finger.
    const offset = position - 1.2;
    finger.proximal.rotation.z = -mirror * offset * SPREAD_STEP * shape.spread;
  });

  const thumb = arm.fingers.thumb;
  const curl = shape.curl.thumb;
  thumb.proximal.rotation.set(
    -curl * THUMB_CURL.proximal,
    mirror * lerp(0.15, 0.85, shape.thumbOut),
    -mirror * lerp(0.35, 1.15, shape.thumbOut),
  );
  thumb.middle.rotation.x = -curl * THUMB_CURL.middle;
  thumb.distal.rotation.x = -curl * THUMB_CURL.distal;
}

/**
 * Analytic two-bone IK. The shoulder aims so the elbow falls on the pole side,
 * then the elbow bends by exactly the angle that lands the wrist on target.
 */
function solveArm(arm: ArmRig, targetWorld: Vector3, poleWorld: Vector3): void {
  const shoulder = arm.shoulder.getWorldPosition(tmp.shoulder);
  const reach = tmp.reach.copy(targetWorld).sub(shoulder);

  const span = UPPER_ARM + FOREARM;
  const distance = Math.min(span - 0.004, Math.max(0.06, reach.length()));
  const direction = tmp.direction.copy(reach).normalize();

  const cosShoulder =
    (UPPER_ARM * UPPER_ARM + distance * distance - FOREARM * FOREARM) /
    (2 * UPPER_ARM * distance);
  const shoulderAngle = Math.acos(Math.min(1, Math.max(-1, cosShoulder)));

  // Axis the arm bends about: perpendicular to the reach and the pole.
  const axis = tmp.axis.copy(direction).cross(poleWorld);
  if (axis.lengthSq() < 1e-6) axis.set(1, 0, 0);
  axis.normalize();

  const upper = tmp.upper.copy(direction).applyAxisAngle(axis, shoulderAngle);

  // Build the upper arm's frame so local +X is the bend axis and the limb runs
  // down local -Y. The elbow is then a pure rotation about the forearm's X.
  tmp.basisX.copy(axis);
  tmp.basisY.copy(upper).negate();
  tmp.basisZ.copy(tmp.basisX).cross(tmp.basisY);
  tmp.matrix.makeBasis(tmp.basisX, tmp.basisY, tmp.basisZ);
  tmp.quat.setFromRotationMatrix(tmp.matrix);

  arm.shoulder.getWorldQuaternion(tmp.parent);
  arm.upperArm.quaternion.copy(tmp.parent.invert().multiply(tmp.quat));
  arm.upperArm.updateMatrixWorld(true);

  // Bend the elbow by exactly the angle that lands the wrist on target: express
  // the elbow-to-target direction in upper-arm space and read the angle off it.
  const elbow = arm.forearm.getWorldPosition(tmp.elbow);
  tmp.localA.copy(targetWorld);
  arm.upperArm.worldToLocal(tmp.localA);
  tmp.localB.copy(elbow);
  arm.upperArm.worldToLocal(tmp.localB);
  tmp.localA.sub(tmp.localB);

  arm.forearm.rotation.set(Math.atan2(-tmp.localA.z, -tmp.localA.y), 0, 0);
  arm.forearm.updateMatrixWorld(true);
}

/**
 * The wrist's intended world rotation: palm along local +Z, fingers down
 * local -Y. Shared by the orientation step and by contact placement, so the
 * frame a contact is computed in is always the frame the hand ends up in.
 *
 * Writes into tmp.matrix/tmp.quat and leaves the result in tmp.quat.
 */
function wristRotation(palmWorld: Vector3, aimWorld: Vector3): Quaternion {
  tmp.basisZ.copy(palmWorld).normalize();
  tmp.basisY.copy(aimWorld).normalize().negate();
  tmp.basisY.addScaledVector(tmp.basisZ, -tmp.basisY.dot(tmp.basisZ));
  // An aim parallel to the palm normal cannot be honoured — it describes no
  // rotation. Fall back rather than emit a degenerate basis.
  if (tmp.basisY.lengthSq() < 1e-6) tmp.basisY.set(0, 1, 0);
  tmp.basisY.normalize();
  tmp.basisX.copy(tmp.basisY).cross(tmp.basisZ);

  tmp.matrix.makeBasis(tmp.basisX, tmp.basisY, tmp.basisZ);
  return tmp.quat.setFromRotationMatrix(tmp.matrix);
}

/** Point the palm where the sign asks, with the fingers aimed sensibly. */
function orientWrist(arm: ArmRig, palmWorld: Vector3, aimWorld: Vector3 | null): void {
  const wrist = arm.wrist.getWorldPosition(tmp.wrist);
  const elbow = arm.forearm.getWorldPosition(tmp.elbow);

  // Default finger aim continues the forearm — what a relaxed hand does.
  const aim = aimWorld
    ? tmp.aim.copy(aimWorld)
    : tmp.aim.copy(wrist).sub(elbow);

  const rotation = wristRotation(palmWorld, aim);
  arm.forearm.getWorldQuaternion(tmp.parent);
  arm.wrist.quaternion.copy(tmp.parent.invert().multiply(rotation));
}

function applyNonManual(rig: Rig, nmf: Required<NonManual>): void {
  rig.head.rotation.set(nmf.headNod * 0.3, -nmf.headTurn * 0.45, -nmf.headTilt * 0.48);

  // A furrow draws the brows down and inward; a raise lifts them flat. Kept
  // shallow so a question face reads as a question, not as anger.
  const lift = nmf.brow * 0.018;
  const inward = Math.max(0, -nmf.brow) * 0.006;
  // z sits the brows proud of the brow ridge; inside it they would be swallowed.
  rig.browLeft.position.set(-0.04 + inward, 0.134 + lift, 0.1);
  rig.browRight.position.set(0.04 - inward, 0.134 + lift, 0.1);
  rig.browLeft.rotation.z = nmf.brow * 0.16;
  rig.browRight.rotation.z = -nmf.brow * 0.16;

  rig.jaw.position.y = 0.055 - nmf.mouthOpen * 0.016;
  rig.jaw.scale.y = 1 + nmf.mouthOpen * 1.6;
}

/**
 * One end of a hand's motion in world space.
 *
 * A contact end lands the *touching part of this hand* on the weak hand's
 * named point. The touching fingertip's real offset from the wrist is measured
 * off the rig at this handshape and rotated into the hand's intended
 * orientation, so a curled finger — which sits centimetres to the side of the
 * wrist axis, not merely nearer along it — lands where it actually is.
 */
function endpointWorld(
  arm: ArmRig,
  weak: ArmRig,
  end: HandTarget,
  palm: Vector3,
  aim: Vector3,
  out: Vector3,
): Vector3 {
  if (!end.weakHandRelative || !end.contact) return out.copy(end.base);

  // Where the touching fingertip sits relative to the wrist, in the wrist's
  // own frame. Independent of where the arm is, so it can be read before the
  // arm is solved.
  fingertipWorld(arm, end.touchWith, tmp.touch);
  arm.wrist.worldToLocal(tmp.touch);
  tmp.touch.applyQuaternion(wristRotation(palm, aim));

  contactPointWorld(weak, end.contact, out);
  return out.sub(tmp.touch).add(end.offset);
}

/**
 * The weak hand is solved first so a dominant-hand contact can be resolved
 * against where its fingertips actually ended up — the BANZSL alphabet is built
 * on exactly that relationship. Both ends of a blend are resolved and lerped,
 * so a movement letter glides from contact to contact.
 */
export function applyPose(rig: Rig, pose: ResolvedPose): void {
  // Both handshapes first: contact placement measures the dominant hand's own
  // fingertip offset, which is only meaningful once its shape is set.
  applyHandShape(rig.arms.left, pose.left.shape);
  applyHandShape(rig.arms.right, pose.right.shape);

  const leftTarget = tmp.target
    .copy(pose.left.from.base)
    .lerp(pose.left.to.base, pose.left.blend);
  solveArm(rig.arms.left, leftTarget, POLE.left);
  orientWrist(rig.arms.left, pose.left.palm, pose.left.aim);
  rig.root.updateMatrixWorld(true);

  const aim = pose.right.aim ?? tmp.contactAim.set(0, 1, 0);
  const { left: weak, right: arm } = rig.arms;
  endpointWorld(arm, weak, pose.right.from, pose.right.palm, aim, tmp.target);
  endpointWorld(arm, weak, pose.right.to, pose.right.palm, aim, tmp.targetB);
  const target = tmp.target.lerp(tmp.targetB, pose.right.blend);

  solveArm(arm, target, POLE.right);
  orientWrist(arm, pose.right.palm, pose.right.aim);

  applyNonManual(rig, pose.nmf);
  rig.root.updateMatrixWorld(true);
}
